/* Hook encargado de hacer la consulta a la base y
traer los creditos

Autor: Benjamín León
Fecha: 2024-11-09
*/
import { useState } from 'react'

import { customFetch } from '../utils/customFetch.ts'
import Credit from '../interfaces/Credit.ts'
import { formatWithThousandSep } from '../utils/utils.ts'

import { makeUrlUnifiedCredit, makeUrlCreditDetail, makeUrlCreditVehCli } from './connectionData.tsx'

const useCreditFetcher = () => {
  const [errorCredit, setErrorCredit] = useState<Error | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * Fetch credits - now uses unified endpoint that includes Autofin credits
   * @param orgId Organization ID
   * @param status Credit state
   * @returns Array of credits (both manual and Autofin)
   */
  const fetchCredits = async (orgId: string, status: number) => {
    setLoading(true)

    try {
      // Use unified endpoint that returns both manual and Autofin credits
      const apiUrl = makeUrlUnifiedCredit(orgId, status)
      const credits = await customFetch<Credit[]>(apiUrl)

      setErrorCredit(null)

      return credits
    } catch (err) {
      // Assert the error as an instance of Error
      const myErr = err instanceof Error ? err : new Error('An unknown error occurred')
      setErrorCredit(myErr)

      return []
    } finally {
      setLoading(false)
    }
  }
  const fetchCreditDetail = async (id: string) => {
    setLoading(true)

    try {
      const apiUrl = makeUrlCreditDetail(id)
      const credit = await customFetch<Credit>(apiUrl)
      setErrorCredit(null)

      return credit
    } catch (err) {
      // Assert the error as an instance of Error
      const myErr = err instanceof Error ? err : new Error('An unknown error occurred')
      setErrorCredit(myErr)

      return null
    } finally {
      setLoading(false)
    }
  }

  const fetchCreditsByVehicleClient = async (vehicleId: string, clientId: string) => {
    setLoading(true)
    const params = new URLSearchParams({
      vehicle: vehicleId,
      client: clientId,
    })
    try {
      const apiUrl = makeUrlCreditVehCli(params)
      const credits = await customFetch<Credit[]>(apiUrl)
      setErrorCredit(null)

      return credits
    } catch (err) {
      // Assert the error as an instance of Error
      const myErr = err instanceof Error ? err : new Error('An unknown error occurred')
      setErrorCredit(myErr)

      return []
    } finally {
      setLoading(false)
    }
  }
  const fetchCreditFormatDetail = async (id: string) => {
    setLoading(true)

    try {
      const apiUrl = makeUrlCreditDetail(id)
      const credit = await customFetch<Credit>(apiUrl)

      const amount_format = formatWithThousandSep(credit.amount)
      const fee_format = formatWithThousandSep(credit.fee)
      const observations_format = credit?.observations.join('\n')
      const paymentsQty_format = formatWithThousandSep(credit.paymentsQty ?? 0)
      const paymentsAmount_format = formatWithThousandSep(credit.paymentsAmount ?? 0)
      const rate_format = formatWithThousandSep(credit.rate ?? 0)
      const balance_format = formatWithThousandSep(credit.balance ?? 0)
      const price_format = formatWithThousandSep(credit.price ?? 0)
      const salary_format = formatWithThousandSep(credit.salary ?? 0)

      const creditFormat = {
        ...credit,
        amount_format: amount_format,
        fee_format: fee_format,
        observations_format: observations_format,
        paymentsQty_format: paymentsQty_format,
        paymentsAmount_format: paymentsAmount_format,
        rate_format: rate_format,
        balance_format: balance_format,
        price_format: price_format,
        salary_format: salary_format,
      }

      setErrorCredit(null)

      return creditFormat
    } catch (err) {
      // Assert the error as an instance of Error
      const myErr = err instanceof Error ? err : new Error('An unknown error occurred')
      setErrorCredit(myErr)

      return null
    } finally {
      setLoading(false)
    }
  }

  // Exponemos estas variables al exterior
  return { errorCredit, loading, fetchCredits, fetchCreditDetail, fetchCreditsByVehicleClient, fetchCreditFormatDetail }
}
export default useCreditFetcher
